'use client';
import { useFormatter, useTranslations } from 'next-intl';
import React, { type ReactNode, useMemo, useState } from 'react';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { PostStatusTag } from '@/app/components/shared/PostStatusTag';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { Avatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { SITE } from '@/app/constants';
import { useDeleteElements, useDevice } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ElementType, ImagePrompt, VideoPrompt } from '@/app/types';
import { cn, convertViduV1ElementToElementType, reportEvent } from '@/app/utils';
import type { ElementInfo } from '../types';

interface ElementCardProps {
  onEdit: (element: ElementType) => void;
  onViewDetails: (element: ElementType) => void;
  onDelete: (ids: string[]) => void;
  sourceElement?: ElementInfo;
  handlePublish: (element: ElementType) => void;
  isTeamSpace?: boolean;
  creatorMeta?: {
    id: string;
    nickname?: string;
    avatar?: string;
  };
  actionBeforePublish?: ReactNode;
  className?: string;
}

export const ElementCard = React.forwardRef<HTMLDivElement, ElementCardProps>(
  (
    {
      onEdit,
      onViewDetails,
      onDelete,
      sourceElement,
      handlePublish,
      isTeamSpace = false,
      creatorMeta,
      actionBeforePublish,
      className,
    },
    ref,
  ) => {
    const format = useFormatter();
    const t = useTranslations('elements');
    const [menuOpen, setMenuOpen] = useState(false);

    const { isMobile } = useDevice();

    const { referenceModalFromPage } = useStore((state) => ({
      referenceModalFromPage: state.referenceModalFromPage,
    }));

    const { handleDelete } = useDeleteElements({
      onSuccess: onDelete,
    });

    const goToDelete = () => {
      reportEvent('element_operation', {
        operation_type: 'delete',
      });

      if (!element) return;
      handleDelete(element);
    };

    const element = useMemo(() => {
      if (!sourceElement) {
        return null;
      }
      return convertViduV1ElementToElementType(sourceElement);
    }, [sourceElement]);

    // 获取图片 prompts（用于显示）
    const [mediaPrompts, maskSrc] = useMemo(() => {
      if (!element) return [[], ''];
      if (element.modality === 'text') return [[], ''];
      if (element.modality === 'image' || element.modality === 'video') {
        const mediaPrompts = (element.optionalAssets || []).filter((asset): asset is ImagePrompt | VideoPrompt => {
          return asset !== null && ('sourceImage' in asset || 'video' in asset);
        });
        // 兼容后端返回空数组的情况
        if (!mediaPrompts.length) {
          return [[], ''];
        }
        const maskSrc =
          'sourceImagePreview' in mediaPrompts[0]
            ? mediaPrompts[0].sourceImagePreview
            : 'coverImagePreview' in mediaPrompts[0]
              ? mediaPrompts[0].coverImagePreview
              : '';
        return [mediaPrompts, maskSrc];
      }
      return [[], ''];
    }, [element]);

    if (!element) {
      return null;
    }

    const handleEdit = () => {
      onEdit?.(element);
      reportEvent('element_operation', {
        operation_type: 'edit',
      });
    };
    const handleView = () => {
      onViewDetails?.(element);
      reportEvent('element_operation', {
        operation_type: 'view_detail',
      });
    };

    const onDropdownOpenChange = (value: boolean) => {
      setMenuOpen(value);
    };

    return (
      <div
        className={cn(
          "relative flex w-full cursor-pointer items-center justify-between overflow-hidden rounded-16 border border-system-white08 bg-system-white04 px-5 py-4 backdrop-blur-xl transition-all before:absolute before:inset-0 before:z-[-1] before:scale-[2.5] before:bg-center before:bg-cover before:bg-dynamic before:bg-no-repeat before:opacity-[.12] before:blur-[15px] before:content-[''] hover:bg-system-white08",
          className,
        )}
        onClick={() => {
          onViewDetails?.(element);
        }}
        ref={ref}
        style={
          maskSrc
            ? ({
                '--bg-image-url': `url(${maskSrc})`,
              } as React.CSSProperties)
            : undefined
        }
      >
        <div className="flex flex-1 flex-col justify-between gap-4 overflow-hidden pr-4">
          <div className="flex flex-col gap-1 overflow-hidden">
            <div className="flex items-center gap-2 overflow-hidden">
              {isTeamSpace && sourceElement?.creator_id && (
                <Avatar
                  id={creatorMeta?.id || sourceElement.creator_id}
                  nickname={creatorMeta?.nickname}
                  url={creatorMeta?.avatar}
                  className="size-5 flex-shrink-0"
                  nameClassName="text-[10px]"
                />
              )}
              <CustomTooltip
                sideOffset={2}
                contentClassName="text-xs"
                content={element.name}
                showOnlyOnOverflow={true}
                maxLines={1}
              >
                <span className="truncate font-medium text-base text-system-white">{element.name}</span>
              </CustomTooltip>
              {element.type === 'preset' && (
                <div className="flex-shrink-0 rounded-full bg-system-blue02 px-2 py-0.5 text-system-white text-xs">
                  {t('demo')}
                </div>
              )}
              {element.share_meta && element.share_meta.status !== 'unspecified' && (
                <PostStatusTag tag={element.share_meta} className="flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="truncate text-system-white32 text-xs">
                {t('recentEdit')}
                {format.dateTime(new Date(element.updated_at ?? 0), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {actionBeforePublish}
            {!isTeamSpace &&
              SITE !== 'china' &&
              element.type === 'user' &&
              element.voice?.is_my !== true &&
              !['approved', 'rejected'].includes(element?.share_meta?.status || '') && (
                <CustomTooltip
                  sideOffset={2}
                  contentClassName="text-xs"
                  content={!isMobile && t('submit.submitTip')}
                  disableHoverableContent={true}
                >
                  <Button
                    variant="text"
                    size="original"
                    className="size-8 rounded-full bg-system-white08 p-2.5 transition-colors hover:bg-system-white16 disabled:opacity-40 sm:h-8 sm:w-8 sm:p-2"
                    disabled={element.share_meta?.status === 'pending'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePublish?.(element);
                      reportEvent('publish_reference_click', {
                        from_page: referenceModalFromPage || '',
                      });
                    }}
                  >
                    <BaseLineSubmit3 className="text-base" />
                  </Button>
                </CustomTooltip>
              )}
            <DropdownMenu open={menuOpen} onOpenChange={onDropdownOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="text"
                  size="original"
                  className="size-8 rounded-full bg-system-white08 p-2.5 transition-colors hover:bg-system-white16 sm:h-8 sm:w-8 sm:p-2"
                >
                  <BaseLineMore className="text-xl" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="center"
                sideOffset={4}
                onPointerDownOutside={() => setMenuOpen(false)}
                collisionPadding={{
                  left: 12,
                  right: 16,
                }}
              >
                {element.type === 'user' && !['approved', 'pending'].includes(element?.share_meta?.status || '') && (
                  <DropdownMenuItem
                    className="min-w-[126px] hover:bg-[#515765]"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleEdit();
                      setMenuOpen(false);
                    }}
                  >
                    {t('edit')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="min-w-[126px] hover:bg-[#515765]"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleView();
                    setMenuOpen(false);
                  }}
                >
                  {t('details')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="min-w-[126px] hover:bg-[#515765]"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setMenuOpen(false);
                    goToDelete();
                  }}
                >
                  {t('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="relative mt-2 mr-2 size-20 flex-shrink-0">
          {mediaPrompts.length > 0 ? (
            <div className="relative h-full w-full">
              {mediaPrompts
                .slice(0, 3)
                .reverse()
                .map((it, idx, arr) => {
                  const actualIdx = arr.length - 1 - idx; // 0 是最顶层（原始数组的第一个），1 是中间，2 是最底层
                  const count = arr.length;
                  const key = 'sourceImagePreview' in it ? it.sourceImagePreview : it.videoPreview || it.video;

                  let transform = '';
                  if (count === 2) {
                    // 两张图：底层（idx=0, actualIdx=1）向右旋转，顶层（idx=1, actualIdx=0）不旋转
                    if (actualIdx === 1) {
                      transform = 'rotate(4deg) translate(2px, 2px)';
                    }
                  } else if (count === 3) {
                    // 三张图：最底层（idx=0, actualIdx=2）向右旋转，中间（idx=1, actualIdx=1）不旋转，最顶层（idx=2, actualIdx=0）向左旋转
                    if (actualIdx === 2) {
                      transform = 'rotate(4deg) translate(2px, 2px)';
                    } else if (actualIdx === 0) {
                      transform = 'rotate(-4deg) translate(-2px, 2px)';
                    }
                  } else {
                    // 单张图或默认情况
                    const offset = actualIdx * 6;
                    transform = `translate(${offset}px, -${offset}px)`;
                  }

                  return (
                    <div
                      key={key}
                      className="absolute inset-0"
                      style={{
                        transform,
                        zIndex: idx,
                      }}
                    >
                      <MediaPromptPreview
                        prompt={it}
                        className="h-full w-full rounded-8 border border-system-white16"
                      />
                    </div>
                  );
                })}
              {mediaPrompts.length > 1 && (
                <div className="-right-2 -top-2 absolute z-10 flex h-6 w-6 items-center justify-center rounded-full border border-system-white08 bg-[#1A1D21] font-bold text-white text-xs shadow-lg sm:h-5 sm:w-5 sm:text-[10px]">
                  {mediaPrompts.length}
                </div>
              )}
            </div>
          ) : (
            <TextElementCover name={element.name} className="rounded-8 border border-system-white16" />
          )}
        </div>
      </div>
    );
  },
);
